import { RowStatusMarkComponent } from '@modules/team-list-state/row-status-mark.component';
import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { EventsService } from '@modules/calendar-app/services/events.service';
import { CalendarAppStateService } from '@modules/calendar-app/services/state.service';
import { EventsFilters } from '@modules/calendar-app/types/events-filters';
import { BaseStitchListComponent, type StitchListItem } from '@modules/common/components/list/base-stitch-list.component';
import { ListItemSeparatorComponent } from '@modules/common/components/list/list-items-separator/list-items-separator.component';
import { ListLoadErrorComponent } from '@modules/common/components/list/list-load-error/list-load-error.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { ListSelectionBarComponent } from '@modules/common/components/list/list-selection-bar/list-selection-bar.component';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { ScrollToPosition } from '@modules/common/services/scroll-to-index.injection-token';
import { type StitchCardSelection, type StitchCardVariant } from '@modules/common/utils/stitch-card-variant';
import { SelectableListDirective } from '@modules/drag-n-drop/directives/selectable-list.directive';
import { SelectableItemDirective } from '@modules/drag-n-drop/directives/selectable.directive';
import { type SelectableOpenEvent } from '@modules/drag-n-drop/types/selectable-open-event';
import { EventComponent } from '@modules/elements/components/event/event.component';
import { ClickDirective } from '@modules/utils/directives/click/click.directive';
import type { KnotSource } from '@sharedlib/common/types/knowledge/knot-source';
import { StitchType } from '@sharedlib/common/types/StitchType';
import type { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { BehaviorSubject, merge, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/** List row for an event; the calendar-location badge is built by the row component (app-event). */
export type EventListItem = StitchListItem<CalendarEventSummaryDto>;

@Component({
  selector: 'app-events-list',
  templateUrl: './events-list.component.html',
  styleUrls: ['./events-list.component.less'],
  providers: [{ provide: ScrollToPosition, useFactory: () => new BehaviorSubject<number>(null) }],
  standalone: true,
  imports: [
    RowStatusMarkComponent,
    NgTemplateOutlet,
    ListLoadErrorComponent,
    NoContentStubComponent,
    LoadingIndicatorComponent,
    ListItemSeparatorComponent,
    SelectableListDirective,
    EventComponent,
    SelectableItemDirective,
    ClickDirective,
    ListSelectionBarComponent,
    Pagination2Component,
  ],
})
export class EventsListComponent
  extends BaseStitchListComponent<CalendarEventSummaryDto>
  implements OnInit, OnChanges, OnDestroy
{
  /** Show per-row calendar-location badges (search results, where results span calendars). */
  public readonly showLocationBadge = input<boolean>(false);
  public readonly tenantItems = input<ReadonlyArray<{ readonly accountId?: string }>>();

  /** Show each row's hover quick actions; off for lists that only preview their items. */
  public readonly actionsButtonEnabled = input<boolean>(true);

  /** Container treatment forwarded to every card; the knowledge hub asks for `'card'`. */
  public readonly variant = input<StitchCardVariant>('row');

  /** Id of the item open in the dynamic panel, forwarded so that card draws itself as selected. */
  public readonly previewedItemId = input<string | null>(null);

  /** What each card draws as selected, forwarded; the knowledge panel asks for the list's own selection. */
  public readonly cardSelection = input<StitchCardSelection>('preview');

  /** The hub draws its related items as cards; every other caller keeps the app's flush row. */
  protected readonly isCard = computed(() => this.variant() === 'card');

  public isPendingReview(item: CalendarEventSummaryDto): boolean {
    return 'pendingReview' in item && item.pendingReview === true;
  }

  public isPendingAssignment(item: CalendarEventSummaryDto): boolean {
    return 'pendingAssignment' in item && item.pendingAssignment === true;
  }

  public items: EventListItem[] = [];

  @Input()
  public override filters: Partial<EventsFilters> = {};
  @Input()
  public withTags = false;
  @Input()
  public withKnots = false;
  @Input()
  public knotsSource: KnotSource;
  @Input()
  public debug: 'score';

  @Output()
  public readonly clickEvent = new EventEmitter<CalendarEventSummaryDto>();
  @Output()
  public readonly doubleClickEvent = new EventEmitter<CalendarEventSummaryDto>();

  @ViewChildren(EventComponent, { read: ElementRef<HTMLElement> })
  public override itemElements: QueryList<ElementRef<HTMLElement>>;

  protected readonly eventsService = inject(EventsService);
  protected readonly calendarAppStateService = inject(CalendarAppStateService);

  /**
   * SBUX-829: an ALL-DAY boundary already IS a display-frame value — `@TransformAllDayDate` decoded it
   * into a local-midnight carrier of its stored date on the way in — so it is returned untouched;
   * projecting it (or normalizing it again, which is not idempotent) shifts the heading a day. A timed
   * boundary is a real instant and takes the base projection.
   */
  protected override getDateSectionValue(item: CalendarEventSummaryDto, value: Date): Date {
    return item.allDay ? value : super.getDateSectionValue(item, value);
  }

  protected override stitchSignalSpaceIds(): ReadonlyArray<string> {
    return [
      ...new Set([
        ...super.stitchSignalSpaceIds(),
        ...(this.tenantItems() ?? []).flatMap(({ accountId }) => (accountId ? [accountId] : [])),
      ]),
    ];
  }

  protected override getStitchType(): StitchType {
    return StitchType.EVENT;
  }

  public override ngOnInit() {
    super.ngOnInit();
    this.subscribeToRefresh();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ((changes.filters && !changes.filters.isFirstChange()) ||
        (changes.tenantItems && !changes.tenantItems.isFirstChange())) {
      this.resetItems();
    }
  }

  protected subscribeToRefresh(): void {
    merge(this.calendarAppStateService.getRefreshAll(), this.eventsService.getRefresh())
      .pipe(takeUntil(this.alive))
      .subscribe(() => {
        this.loadPage(this.page);
      });
  }

  protected override getItems(
    offset: number,
    limit: number,
  ): Observable<{ items: CalendarEventSummaryDto[]; count: number }> {
    return this.eventsService.search(
      {
        ...this.filters,
        archived: this.filters?.archived ?? false,
        deleted: this.filters?.deleted ?? false,
        limit,
        offset,
      },
      {
        withTags: this.withTags,
        withKnots: this.withKnots,
        withConnections: false,
        knotsSource: this.knotsSource,
        tenantItems: this.tenantItems(),
      },
    );
  }

  protected handleDoubleClickItem(appointment: CalendarEventSummaryDto) {
    if (!appointment) {
      return;
    }
    this.doubleClickEvent.emit(appointment);
  }

  protected handleOpenSelected(event: SelectableOpenEvent<CalendarEventSummaryDto>): void {
    const appointment = event.item.data;

    if (!appointment) {
      return;
    }

    this.clickEvent.emit(appointment);
  }
}
